import React, {useEffect, useMemo} from 'react';
import {unstable_HistoryRouter as HistoryRouter} from 'react-router-dom';
import {createBrowserHistory} from 'history';
import type {History} from '@remix-run/router';
import ApplicationViews from "./views";
import {
    BizkeytechDialogs,
    BizkeytechReduxStore,
    DataGridProvider,
    DateUtils,
    DynamicColumnApplications,
    DynamicColumnsProvider,
    DynamicColumnsProviderProps,
    QueryBuilderProvider,
    SEO as ApplicationSEO,
    ThemeProvider,
    ThemeProviderProps,
    ToastContainer,
    useAuthorizationToken,
} from "../@bizkeytech";
import ApplicationMiddlewares from "./middlewares";
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import {Provider as ReduxProvider} from 'react-redux';
import ApiService from '../core/services/api';
import UIUtils from "./utils";
import ApplicationDialogs from "./dialogs";
import ReduxStore from "./redux";
import {HelmetProvider} from "react-helmet-async";
import EnvService from "../core/services/env";
import ApiEndpoints from "../core/models/api-endpoints";
import {CachingServiceLocalStorageKeys} from "../core/services/caching";
import {Slide} from "react-toastify";
import {Theme, useMediaQuery} from "@mui/material";
import theme from "./theme";


const Application = () => {
    const history = useMemo(() => createBrowserHistory() as unknown as History, [])
    const authorizationToken = useAuthorizationToken();

    /**
     * Injects the authentication token in the request headers of the API service.
     */
    useEffect(() => {
        ApiService.injectInterceptors({
            request: [{
                id: 'auth-token',
                interceptor: {
                    onFulfilled: (config) => {
                        config.headers.Authorization = config.headers.Authorization ?? authorizationToken;
                        return config;
                    }
                },
            }],
        })
    }, [authorizationToken]);

    const dynamicColumnsFetchColumnsMap = useMemo<DynamicColumnsProviderProps['fetchColumnsMap']>(() => ({
        [DynamicColumnApplications.default]: ApiService.fetchDynamicColumns.bind(ApiService),
    }), [])

    const dynamicColumnsGetFilterOptionsMap = useMemo<DynamicColumnsProviderProps['getFilterOptionsMap']>(() => ({
        [DynamicColumnApplications.default]: ApiEndpoints.reportingTools.baseUrl,
    }), [])

    const themeProviderFetchPartnerColorScheme = useMemo<ThemeProviderProps['fetchPartnerColorScheme']>(() =>
            ApiService.getPartnerInformation.bind(ApiService),
        [])

    const themes = useMemo<Required<ThemeProviderProps>['themes']>(() => [theme], [])

    return (
        <HelmetProvider>
            <HistoryRouter basename={EnvService.publicUrl} history={history}>
                <ApplicationSEO/>
                <ThemeProvider
                    defaultMode={'system'}
                    authorizationToken={authorizationToken}
                    fetchPartnerColorScheme={themeProviderFetchPartnerColorScheme}
                    themes={themes}
                >
                    <LocalizationProvider dateAdapter={DateUtils.adapter} dateLibInstance={DateUtils.instance}>
                        <DynamicColumnsProvider
                            fetchColumnsMap={dynamicColumnsFetchColumnsMap}
                            getFilterOptionsMap={dynamicColumnsGetFilterOptionsMap}
                        >
                            <QueryBuilderProvider
                                authorizationToken={authorizationToken}
                                applicationName={EnvService.servicesApplicationName}
                            >
                                <DataGridProvider
                                    authorizationToken={authorizationToken}
                                    applicationName={EnvService.servicesApplicationName}
                                    storageKey={CachingServiceLocalStorageKeys.dataGridSavedState}
                                >
                                    <ReduxProvider store={BizkeytechReduxStore}>
                                        <BizkeytechDialogs/>
                                    </ReduxProvider>
                                    <ReduxProvider store={ReduxStore}>
                                        <ApplicationViews/>
                                        <ApplicationDialogs/>
                                    </ReduxProvider>
                                    <ApplicationMiddlewares/>
                                    <ApplicationToastContainers/>
                                </DataGridProvider>
                            </QueryBuilderProvider>
                        </DynamicColumnsProvider>
                    </LocalizationProvider>
                </ThemeProvider>
            </HistoryRouter>
        </HelmetProvider>
    );
}

const ApplicationToastContainers = () => {
    const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

    return (
        <>
            <ToastContainer
                containerId={UIUtils.ToastContainerIDs.default}
                hideProgressBar
                transition={Slide}
                closeButton={false}
                position={isMobile ? 'bottom-center' : 'top-center'}
                draggableDirection={'y'}
                draggablePercent={10}
                autoClose={5000}
            />
            <ToastContainer
                containerId={UIUtils.ToastContainerIDs.api}
                hideProgressBar
                transition={Slide}
                position={isMobile ? 'top-center' : 'top-right'}
                draggableDirection={isMobile ? 'y' : 'x'}
                draggablePercent={10}
                autoClose={5000}
            />
        </>
    );
}

export default Application;
